<template>
  <div class="t-gen-form">
    <el-row :gutter="formConfCopy.gutter">
      <el-form
        :ref="formConfCopy.formRef"
        :disabled="formConf.disabled"
        :label-position="formConfCopy.labelPosition"
        :label-width="formConfCopy.labelWidth + 'px'"
        :model="formModel"
        :rules="formRules"
        :size="formConfCopy.size"
        @submit.native.prevent
      >
        <generate-form-item
          v-for="(item, index) in formConfCopy.fields"
          :key="`${item.key}_${index}`"
          :cid="item.vModel"
          :index="index"
          :item="item"
          :models.sync="formModel"
          :rules="formRules"
          :seq-no="getSeqNo(index, item)"
          :style="isFieldShow(item)"
          @next="nextPage"
          @prev="prevPage"
          @input-change="onInputChange"
          @ocr-success="handleOcrSuccess"
          :pageShowMore="pageShowMore"
          :btnStyle="formConfCopy.theme"
        />
      </el-form>
    </el-row>
  </div>
</template>

<script>
import { handleChangeLabel } from './FormItemLabel'
import _ from 'lodash'
import { isShown } from '@/utils/expression'
import { execRemoteAPI } from '../../../api/execRemoteAPI'
import GenerateFormItem from './GenerateFormItem'
import { throttle } from 'throttle-debounce'
import FormValidateRule from './FormValidateRule'
import { saveFormData } from '@/utils/db'
import { scrollTo } from '@/utils/scroll-to'
import { parameterChildList } from '@/views/form/logic/config'

let seqNo = 0

export default {
  name: 'GenerateForm',
  components: {
    GenerateFormItem
  },
  mixins: [FormValidateRule],
  props: {
    formConf: {
      type: Object,
      required: true
    },
    // 被触发了逻辑显示的字段列表
    logicTriggerItemList: {
      type: Array,
      required: false
    },
    pageFormModel: {
      type: Object,
      required: false
    },
    pageShowMore: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      // 维护一个对象存储 字段名和字段的index 避免查找
      fieldIdIndex: {},
      formConfCopy: _.cloneDeep(this.formConf),
      formModel: _.cloneDeep(this.pageFormModel),
      formRules: {},
      btnLoading: false,
      parameterChildList
    }
  },
  computed: {
    showSubmitBtn() {
      const { theme } = this.formConfCopy
      if (theme) {
        return true
      }
      if (theme && false == theme.showSubmitBtn) {
        return false
      }
      return true
    }
  },
  watch: {
    // 字段集合/配置(必填、逻辑显示)变化时重建校验规则。
    // 用结构签名去重，避免 buildRules 改写 config.regList 触发的深度监听死循环。
    formConfCopy: {
      deep: true,
      handler() {
        const sig = (this.formConfCopy.fields || [])
          .map((f) => `${f.vModel}:${f.config && f.config.required}:${f.logicShowField}`)
          .join('|')
        if (sig === this.__rulesSig) return
        this.__rulesSig = sig
        this.buildRules(this.formConfCopy.fields, this.formRules)
      }
    },
    // 逻辑显示字段被触发/撤销时，重新构建校验规则，确保新显示的必填项也能被校验
    logicTriggerItemList: {
      deep: true,
      handler() {
        this.buildRules(this.formConfCopy.fields, this.formRules)
      }
    }
  },
  created() {
    // 初始化动态数据 比如SELECT等远程数据
    try { this.initDynamicOptions(this.formConfCopy.fields) } catch (e) { console.error('initDynamicOptions 异常', e) }
    // 初始化默认值（若此处异常不能影响后续规则构建）
    try { this.initFormData(this.formConfCopy.fields, this.formModel) } catch (e) { console.error('initFormData 异常', e) }
    try { this.initParamsLogic() } catch (e) { console.error('initParamsLogic 异常', e) }
    // 构建表单校验规则（必须执行，否则必填校验会被整体跳过）
    this.buildRules(this.formConfCopy.fields, this.formRules)
    // 特殊处理分页
    seqNo = this.formConfCopy.startSeqNo || 0
  },
  mounted() {
    // 兜底：确保字段就绪后规则被正确构建（修复部分必填项规则缺失导致可直接提交的问题）
    this.buildRules(this.formConfCopy.fields, this.formRules)
  },
  methods: {
    getKey() {
      return new Date()
    },
    // 初始化表单动态选项
    async initDynamicOptions(componentList) {
      for (const cur of componentList) {
        const config = cur.config
        // 数据为从接口获取
        if (config.optionsType && config.optionsType === 1) {
          execRemoteAPI.get(config.dynamicOptions.url).then((res) => {
            const options = _.get(res.data, config.dynamicOptions.dataPath)
            if (options && _.isArray(options)) {
              const dyOptions = options.map((op) => ({
                value: _.get(op, config.dynamicOptions.valueField),
                label: _.get(op, config.dynamicOptions.labelField)
              }))
              config.options = dyOptions
            }
          })
        }
        // 从字典获取
        if (config.optionsType && config.optionsType === 2) {
          let dictRes = await execRemoteAPI.get(`/system/dict/data/type/${config.dictOptionType}`)
          config.options = dictRes.data.data.map((op) => ({
            value: op.dictValue,
            label: op.dictLabel
          }))
        }
      }
    },
    // 初始化表单默认值
    initFormData(componentList, formData) {
      if (!Array.isArray(componentList)) return
      // 设置默认值
      componentList.forEach((cur, index) => {
        const config = cur.config || {}
        this.fieldIdIndex[cur.vModel] = index
        // 如果存在分页带回的数据 则不再设置默认 0是checkbx radio等的其他输入框
        if (cur.vModel && !formData[cur.vModel] && 0 != formData[cur.vModel]) {
          formData[cur.vModel] = config.defaultValue
          // 单个字段初始化失败不能中断整个循环，否则 created 会被中断、校验规则整体丢失
          try {
            handleChangeLabel(formData, cur, config.defaultValue)
          } catch (e) {
            console.error('初始化label失败, field=', cur.vModel, e)
          }
          try {
            this.logicShowHandle(config.defaultValue, cur.vModel, {})
          } catch (e) {
            console.error('初始化显示逻辑失败, field=', cur.vModel, e)
          }
        }
        if (config.children) this.initFormData(config.children, formData)
      })
    },
    // 判断问卷逻辑规则是否添加了外部参数
    initParamsLogic() {
      const { formConfCopy, parameterChildList } = this
      if (!formConfCopy.logicShowRule) return
      parameterChildList.forEach((cur) => {
        if (cur.value in formConfCopy.logicShowRule) this.logicShowHandle('', cur.value, {})
      })
    },

    // 表单校验规则
    buildRules(componentList, rules) {
      if (!Array.isArray(componentList)) return
      componentList.forEach((cur) => {
        // 逻辑不显示必填问题不校验
        const triggerShow = _.indexOf(this.logicTriggerItemList, cur.formItemId) > -1
        // cur.logicShow 是否逻辑显示字段  triggerShow 已经被触发显示的问题
        // 是逻辑显示字段  并且还没满足条件被显示·
        if (cur.logicShowField !== true || triggerShow) {
          // 单个字段构建失败不能中断整个循环，否则后续字段的必填校验会全部丢失
          try {
            this.buildRule(cur, rules)
          } catch (e) {
            console.error('构建校验规则失败, field=', cur && cur.vModel, e)
          }
        }
      })
    },
    /**
     * 提交/翻页前兜底：确保当前应校验字段都有规则。
     * 返回 false 表示存在必填字段却拿不到规则，此时必须阻止提交，避免静默跳过校验。
     */
    ensureRulesReady() {
      this.buildRules(this.formConfCopy.fields, this.formRules)
      const requiredFields = (this.formConfCopy.fields || []).filter((cur) => {
        const triggerShow = _.indexOf(this.logicTriggerItemList, cur.formItemId) > -1
        const visible = cur.logicShowField !== true || triggerShow
        return visible && cur.vModel && cur.config && cur.config.required
      })
      const missing = requiredFields.filter((cur) => {
        const r = this.formRules[cur.vModel]
        return !Array.isArray(r) || r.length === 0
      })
      if (missing.length) {
        console.error(
          '必填字段缺少校验规则，已阻止提交：',
          missing.map((f) => f.vModel)
        )
        this.$message && this.$message.error('问卷加载异常，请刷新页面后重新填写')
        return false
      }
      return true
    },
    removeRule(cur) {
      delete this.formRules[cur.vModel]
    },
    resetForm() {
      this.formConfCopy = _.cloneDeep(this.formConf)
      this.$refs[this.formConf.formRef].resetFields()
    },
    generateFormSubmit: throttle(3000, true, function () {
      const elForm = this.$refs[this.formConf.formRef]
      if (!elForm) return false
      // 兜底：规则缺失时先重建，重建后仍缺失则拒绝提交
      if (!this.ensureRulesReady()) return false
      try {
        elForm.validate((valid) => {
          if (!valid) {
            // 未选中自动高亮
            setTimeout(() => {
              const isError = document.getElementsByClassName('is-error')
              if (isError[0]) {
                scrollTo(isError[0].offsetTop, 800)
                isError[0].querySelector('input')?.focus()
              }
            }, 100)
            return false
          }
          //开始loading 1s后关闭
          this.btnLoading = true
          setTimeout(() => {
            this.btnLoading = false
          }, 1000)
          // 触发submit事件
          this.$emit('submit', { formModel: this.formModel })
          return true
        })
      } catch (e) {
        // 校验过程异常时视为不通过，阻止非法提交
        console.error('表单校验异常，已阻止提交：', e)
        return false
      }
    }),
    /**
     *  表单发生值变化
     * @param value 值
     * @param field 字段名
     * @param item  发生改变表单组件对象
     */
    onInputChange(value, field, item) {
      this.$emit('on-change', field, value, this.formModel)
      this.$emit('update:page-form-model', this.formModel)
      this.logicShowHandle(value, field, item)
      //临时存储数据到本地 下次进入可以选择回显
      this.tempSaveFormData()
    },
    /**
     * 分页下一页
     */
    nextPage(page) {
      this.switchPage('next', page)
    },

    /**
     * 分页上一页
     */
    prevPage(page) {
      this.switchPage('prev', page)
    },
    /**
     * 切换页
     */
    switchPage(eventName, page) {
      const { formConfCopy } = this
      const elForm = this.$refs[this.formConf.formRef]
      if (!elForm) return false
      // 兜底：规则缺失时先重建，重建后仍缺失则拒绝翻页
      if (!this.ensureRulesReady()) return false
      try {
        elForm.validate((valid) => {
          if (!valid) {
            setTimeout(() => {
              const isError = document.getElementsByClassName('is-error')
              if (isError[0]) isError[0].querySelector('input')?.focus()
            }, 100)
            return false
          }
          this.$emit(eventName, {
            page,
            startSeqNo: this.formConfCopy.startSeqNo,
            endSeqNo: seqNo,
            formModel: this.formModel
          })
          return true
        })
      } catch (e) {
        console.error('翻页校验异常，已阻止翻页：', e)
        return false
      }
    },
    // 字段是否能显示
    isFieldShow(item) {
      //不是逻辑显示字段
      if (!item.logicShowField) {
        item.isFieldShow = true
        return ''
      }
      // 表单被重新渲染 控制逻辑显示隐藏
      const triggerShow = _.indexOf(this.logicTriggerItemList, item.vModel) > -1
      //  或者已经被触发显示了
      item.isFieldShow = triggerShow
      return triggerShow ? '' : 'display:none'
    },
    getSeqNo(index, item) {
      //是否开启显示题号、序号
      if (!this.formConfCopy.theme || !this.formConfCopy.theme.isShowItemQuestionNumber || this.isFieldShow(item)) {
        return null
      }
      // 优先展示序号，再展示题号
      if (this.formConfCopy.theme.showNumber) {
        // 分页开始的序号
        const startSeqNo = this.formConfCopy.startSeqNo || 0
        // 序号等于当前组件索引 + 1 + 起始的分页序号
        seqNo = index + 1 + startSeqNo
      } else if (this.formConfCopy.theme.isShowItemQuestionNumber) {
        return item.questionNumber
      }
      return seqNo
    },
    // 数据变更时 处理逻辑显示相关数据
    logicShowHandle(value, field, item) {
      seqNo = this.formConfCopy.startSeqNo || 0
      // 值被包装过 需要取出原始值
      const { logicShowRule } = this.formConfCopy
      if (!logicShowRule) {
        return
      }
      // 找到该问题需要触发显示的问题 判断逻辑是否成立
      const rules = _.get(logicShowRule, field, [])
      if (rules && Array.isArray(rules)) {
        rules.forEach((r) => {
          // 成立让该对应的问题显示出来
          let flag = false
          try {
            flag = isShown(this.formModel, r.logicExpression)
          } catch (e) {
            console.error('显示逻辑表达式执行失败, field=', field, r && r.logicExpression, e)
            return
          }
          if (flag) {
            // 显示加上值必填等校验
            let triggerFormItem = this.formConfCopy.fields[this.fieldIdIndex[r.triggerFormItemId]]
            // 重新渲染组件 不然必填红点不显示
            if (triggerFormItem) {
              triggerFormItem.key = new Date().getTime()
              this.buildRule(triggerFormItem, this.formRules)
              // 设置默认值
              this.$set(this.formModel, r.triggerFormItemId, triggerFormItem.config.defaultValue)
            }
            // 防止表单重新渲染 display被刷新
            if (Array.isArray(this.logicTriggerItemList)) {
              this.logicTriggerItemList.push(r.triggerFormItemId)
            }
            let triggerEle = document.querySelector(`div[cid="${r.triggerFormItemId}"]`)
            if (triggerEle) {
              triggerEle.style.display = ''
            }
          } else {
            _.remove(this.logicTriggerItemList, (n) => n === r.triggerFormItemId)
            // 移除表单的必填校验
            let triggerFormItem = this.formConfCopy.fields[this.fieldIdIndex[r.triggerFormItemId]]
            if (triggerFormItem) {
              this.removeRule(triggerFormItem)
            }
            // 移除已经填写的值
            delete this.formModel[r.triggerFormItemId]
            let triggerEle = document.querySelector(`div[cid="${r.triggerFormItemId}"]`)
            if (triggerEle) {
              triggerEle.style.display = 'none'
            }
          }
        })
      }
    },
    /**
     * OCR识别完成之后 把数据填充到表单内
     * @param val
     */
    handleOcrSuccess(val) {
      Object.keys(val).forEach((key) => {
        this.$set(this.formModel, key, val[key])
        //更改组件key 让组件重新渲染 不然数据不会显示到表单
        let item = this.formConfCopy.fields[this.fieldIdIndex[key]]
        item.key = new Date() + key
      })
    },
    /**
     * 临时存储到localstore
     */
    tempSaveFormData: throttle(430, true, function () {
      // 是否打开临时存储选项
      if (this.formConf.setting && this.formConf.setting.saveNotSubmitStatus) {
        saveFormData(this.formModel, this.formConf.formKey)
      }
    })
  }
}
</script>
